const RELOAD_STORAGE_KEY = "M365Pulse.ChunkLoadReloadAt";

/**
 * At most MAX_CHUNK_RELOADS automatic reloads happen within this window. After that a chunk failure shows the error
 * instead of reloading again, so a chunk that is still missing after the reloads can't cause a reload loop.
 */
export const CHUNK_RELOAD_GUARD_MS = 5 * 60 * 1000;
export const MAX_CHUNK_RELOADS = 2;

export interface ChunkLoadFailure {
    kind: "js" | "css";
    /** "missing" when the server answered with something else (e.g. index.html), otherwise the DOM event type ("error", "timeout") */
    failureType?: string;
    request?: string;
}

const readStringProperty = (error: Error, key: "code" | "type" | "request") => {
    const value: unknown = Reflect.get(error, key);
    return typeof value === "string" ? value : undefined;
};

/**
 * Rspack rejects a lazy import with a `ChunkLoadError` for JS chunks and with code `CSS_CHUNK_LOAD_FAILED` for extracted CSS chunks.
 * After a deployment, open tabs ask for chunk files that no longer exist. When the server answers with index.html, the browser
 * also reports "Unexpected token '<'" before the import fails.
 */
export const getChunkLoadFailure = (error: unknown): ChunkLoadFailure | undefined => {
    if (!(error instanceof Error)) {
        return undefined;
    }
    const failureType = readStringProperty(error, "type");
    const request = readStringProperty(error, "request");
    if (readStringProperty(error, "code") === "CSS_CHUNK_LOAD_FAILED" || /^Loading CSS chunk \S+ failed/.test(error.message)) {
        return { kind: "css", failureType, request };
    }
    if (error.name === "ChunkLoadError" || /^Loading chunk \S+ failed/.test(error.message)) {
        return { kind: "js", failureType, request };
    }
    return undefined;
};

export type ChunkLoadRecoveryReason = "FirstFailure" | "RepeatedFailure" | "RetryLimitReached" | "Offline" | "StorageUnavailable";

export interface ChunkLoadRecoveryDecision {
    reload: boolean;
    reason: ChunkLoadRecoveryReason;
    lastReloadAgeMs?: number;
    /** reloads already done within CHUNK_RELOAD_GUARD_MS */
    recentReloadCount?: number;
}

/** Reloads from the future (clock moved back) don't count. */
const getRecentReloads = (now: number, reloadTimes: readonly number[]) =>
    reloadTimes.filter((reloadAt) => now - reloadAt >= 0 && now - reloadAt < CHUNK_RELOAD_GUARD_MS);

/**
 * @param reloadTimes times of earlier reloads, or null if sessionStorage can't be read
 */
export const decideChunkLoadRecovery = (now: number, reloadTimes: readonly number[] | null, isOnline: boolean): ChunkLoadRecoveryDecision => {
    if (reloadTimes === null) {
        return { reload: false, reason: "StorageUnavailable" };
    }
    const lastReloadAgeMs = reloadTimes.length === 0 ? undefined : now - Math.max(...reloadTimes);
    const recentReloadCount = getRecentReloads(now, reloadTimes).length;
    if (!isOnline) {
        return { reload: false, reason: "Offline", lastReloadAgeMs, recentReloadCount };
    }
    if (recentReloadCount >= MAX_CHUNK_RELOADS) {
        return { reload: false, reason: "RetryLimitReached", lastReloadAgeMs, recentReloadCount };
    }
    return { reload: true, reason: recentReloadCount === 0 ? "FirstFailure" : "RepeatedFailure", lastReloadAgeMs, recentReloadCount };
};

/** Stored as comma-separated times; a single time (the earlier format) reads as one reload. Unparsable entries are ignored. */
const readReloadTimes = (): number[] | null => {
    try {
        const stored = window.sessionStorage.getItem(RELOAD_STORAGE_KEY) ?? "";
        return stored
            .split(",")
            .filter((entry) => entry.trim() !== "")
            .map(Number)
            .filter((value) => Number.isFinite(value));
    } catch {
        return null;
    }
};

export const getChunkLoadRecoveryDecision = () => decideChunkLoadRecovery(Date.now(), readReloadTimes(), navigator.onLine !== false);

let reloadPending = false;

export const isChunkReloadPending = () => reloadPending;

/**
 * Records the reload in sessionStorage and reloads the page.
 * Returns false without reloading if the record can't be read or written, because the loop guard would not work.
 */
export const reloadForChunkLoadFailure = () => {
    const now = Date.now();
    const reloadTimes = readReloadTimes();
    if (reloadTimes === null) {
        return false;
    }
    try {
        window.sessionStorage.setItem(RELOAD_STORAGE_KEY, [...getRecentReloads(now, reloadTimes), now].join(","));
    } catch {
        return false;
    }
    reloadPending = true;
    window.location.reload();
    return true;
};
